import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ChevronRight, FileText, ShieldCheck, UserCircle } from 'lucide-react'
import { defineField, normaliseRegistrationField } from '@/shared/lib/forms'
import { EditableDetailCard } from '@/admin/components/shared/editable-detail-card'
import type {
  CardFieldItem,
  EditConfig,
} from '@/admin/components/shared/editable-card-types'
import { Card } from '@/shared/components/ui/card'
import { Separator } from '@/shared/components/ui/separator'
import { toastApiError } from '@/shared/lib/api-error'
import { ConsumerShell } from '@/consumer/components/consumer-shell'
import { useFormatters } from '@/shared/lib/formatting'
import {
  useConsumerSession,
  useUpdateConsumerProfile,
} from '@/consumer/hooks/use-consumer-session'

// The account-level fields stored directly on the consumer record. Everything
// else the form submits is a configurable consumer attribute (code_name) and is
// routed to consumer_attribute_values. Email is intentionally absent - it is the
// consumer's login identity and stays read-only.
const ACCOUNT_FIELD_CODES = new Set(['first_name', 'last_name', 'phone_number'])

const LEGAL_LINKS = [
  { to: '/terms-and-conditions', label: 'Terms & Conditions' },
  { to: '/privacy-policy', label: 'Privacy Policy' },
] as const

export default function AccountPage() {
  const { t } = useTranslation()
  const { data: session } = useConsumerSession()
  const updateProfile = useUpdateConsumerProfile()
  const { formatDate } = useFormatters()

  const profileItems: CardFieldItem[] = [
    {
      field: defineField({
        code_name: 'first_name',
        display_name: 'First name',
        field_type: 'TEXT',
      }),
      value: session?.first_name ?? '',
    },
    {
      field: defineField({
        code_name: 'last_name',
        display_name: 'Last name',
        field_type: 'TEXT',
      }),
      value: session?.last_name ?? '',
    },
    {
      field: defineField({
        code_name: 'email',
        display_name: 'Email',
        field_type: 'EMAIL',
      }),
      value: session?.email ?? '',
      readOnly: true,
    },
    {
      field: defineField({
        code_name: 'phone_number',
        display_name: 'Phone number',
        field_type: 'PHONE',
        validation_rules: [
          {
            validation_type: 'max_length',
            max_length: 20,
            failure_message: t(
              'validation.phoneMaxLength',
              'Must be 20 characters or fewer.',
            ),
          },
        ],
      }),
      value: session?.phone_number ?? '',
    },
    // The consumer's configurable personal attributes for the active tenant
    // (e.g. the composite Address field). Hidden component attributes are
    // filtered out, mirroring the registration form.
    ...(session?.consumer_fields ?? [])
      .filter((f) => !f.configuration_data?.hidden)
      .map((f) => ({
        field: normaliseRegistrationField(f),
        value: session?.consumer_attribute_values?.[f.code_name],
      })),
  ]

  const editConfig: EditConfig = {
    onSave: async (values) => {
      // Split the submitted values: account-level fields go top-level, every
      // other key is a configurable consumer attribute routed to
      // consumer_attribute_values.
      const consumerAttributeValues: Record<string, unknown> = {}
      for (const [key, value] of Object.entries(values)) {
        if (!ACCOUNT_FIELD_CODES.has(key)) {
          consumerAttributeValues[key] = value
        }
      }
      try {
        await updateProfile.mutateAsync({
          first_name: String(values.first_name ?? '').trim(),
          last_name: String(values.last_name ?? '').trim(),
          phone_number: String(values.phone_number ?? '').trim(),
          ...(Object.keys(consumerAttributeValues).length > 0
            ? { consumer_attribute_values: consumerAttributeValues }
            : {}),
        })
      } catch (err) {
        toastApiError(err, 'Could not save your changes. Please try again.')
        throw err
      }
    },
    isPending: updateProfile.isPending,
    canEdit: true,
  }

  const termsAccepted = session?.accepted_terms_at
    ? formatDate(String(session.accepted_terms_at))
    : null

  return (
    <ConsumerShell>
      <div className="mx-auto max-w-2xl">
        <h1 className="mb-6 font-display text-[32px] text-[var(--ink)]">
          My account
        </h1>

        <div className="flex flex-col gap-6">
          {/* Address lookup context is supplied at the app root
              (ConsumerAddressLookupProvider in App.tsx), which wraps every
              route including /account - no per-page provider needed. */}
          <EditableDetailCard
            title="Personal information"
            icon={<UserCircle size={16} />}
            items={profileItems}
            editConfig={editConfig}
          />

          <Card className="p-0">
            <div className="flex items-center gap-2 border-b-[0.5px] border-b-[var(--rule)] px-4 py-3">
              <FileText size={16} className="text-[var(--ink-3)]" />
              <span className="text-sm font-medium text-[var(--ink)]">Legal</span>
            </div>
            <div>
              {LEGAL_LINKS.map((item, i) => (
                <div key={item.to}>
                  {i > 0 && <Separator />}
                  <Link
                    to={item.to}
                    className="flex items-center justify-between px-4 py-3 text-sm text-[var(--ink-2)] transition-colors hover:bg-[var(--bg-subtle)]"
                  >
                    {item.label}
                    <ChevronRight size={16} className="text-[var(--ink-3)]" />
                  </Link>
                </div>
              ))}
            </div>
            {termsAccepted && (
              <div className="flex items-center gap-2 border-t-[0.5px] border-t-[var(--rule)] px-4 py-3 text-[12px] text-[var(--ink-3)]">
                <ShieldCheck size={14} className="text-[var(--green)]" />
                Terms &amp; Conditions accepted on {termsAccepted}
              </div>
            )}
          </Card>
        </div>
      </div>
    </ConsumerShell>
  )
}
